import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Refresh } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { LereTabComponentProps } from '~/components/LERE/types';

import { heatmapSurchargeQueryKeys } from '~/hooks/apis/lere/heatmapSurchargeQueryKeys';
import { useGetHeatmapSurchargeCalendar } from '~/hooks/apis/lere/useGetHeatmapSurchargeCalendar';
import { useGetHeatmapSurchargeOffers } from '~/hooks/apis/lere/useGetHeatmapSurchargeOffers';

import { HeatmapSurchargeApplication, HeatmapSurchargeApproval } from '~/types/heatmapSurcharge';

import HeatmapApplicationStatus from './HeatmapApplicationStatus';
import HeatmapApprovalDialog from './HeatmapApprovalDialog';
import HeatmapBulkApprovalDialog from './HeatmapBulkApprovalDialog';
import HeatmapCalendar from './HeatmapCalendar';
import HeatmapPdfLink from './HeatmapPdfLink';
import HeatmapRecommendations from './HeatmapRecommendations';
import { getApplicationJobs, getApprovalKey, getHeatmapCalendarDateStatuses, isApprovalBlocked } from './approval';
import { formatMonth } from './utils';

const pricingMessages = {
  unavailable:
    'Current pricing is unavailable or has expired. The forecast remains visible; prices will return after the pricing refresh.',
  not_covered: 'Current pricing does not yet cover this offer. The forecast remains available for review.',
  forecast_changed:
    'A newer forecast has been imported. Prices will be available once the pricing refresh catches up with this forecast.',
};

export default function HeatmapSurcharge(_props: LereTabComponentProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const offerId = params.get('offer_id') || undefined;
  const roomTypeId = params.get('room_type_id') || undefined;
  const checkInMonth = params.get('check_in_month') || undefined;
  const offers = useGetHeatmapSurchargeOffers();
  const calendarQuery = useGetHeatmapSurchargeCalendar(offerId, roomTypeId, checkInMonth);
  const calendar = calendarQuery.data;
  const [selectedDate, setSelectedDate] = useState<string>();
  const [recommendationScrollRequest, setRecommendationScrollRequest] = useState<{
    date: string;
    sequence: number;
  }>();
  const recommendationScrollSequence = useRef(0);
  const [approval, setApproval] = useState<HeatmapSurchargeApproval>();
  const [bulkApprovals, setBulkApprovals] = useState<Array<HeatmapSurchargeApproval>>();
  const [bulkSelectedDates, setBulkSelectedDates] = useState<Set<string>>(new Set());
  const [applications, setApplications] = useState<Record<string, HeatmapSurchargeApplication>>({});

  const updateApplicationStatus = useCallback((jobId: string, status: HeatmapSurchargeApplication['status']) => {
    setApplications((previous) => {
      const entry = Object.entries(previous).find(([, application]) => application.jobId === jobId);
      if (!entry || entry[1].status === status) return previous;
      return { ...previous, [entry[0]]: { ...entry[1], status } };
    });
  }, []);

  function selectFilters(next: { offerId?: string; roomTypeId?: string; checkInMonth?: string }) {
    const search = new URLSearchParams();
    if (next.offerId) search.set('offer_id', next.offerId);
    if (next.roomTypeId) search.set('room_type_id', next.roomTypeId);
    if (next.checkInMonth) search.set('check_in_month', next.checkInMonth);
    setSelectedDate(undefined);
    setRecommendationScrollRequest(undefined);
    setApproval(undefined);
    setBulkApprovals(undefined);
    setBulkSelectedDates(new Set());
    navigate({ pathname: location.pathname, search: search.toString() }, { replace: true });
  }

  const selectedOffer = offers.data?.offers.find((offer) => offer.offerId === offerId) ?? null;
  const selectedRoom = calendar?.roomTypes.find((room) => room.roomTypeId === calendar.selectedRoomTypeId);
  const activeDate = calendar?.roomNights.some((night) => night.travelDate === selectedDate)
    ? selectedDate
    : (calendar?.recommendations[0]?.travelDate ?? calendar?.roomNights[0]?.travelDate);
  const calendarDateStatuses = useMemo(
    () => (calendar ? getHeatmapCalendarDateStatuses(calendar, applications) : new Map()),
    [applications, calendar],
  );
  const readyDates = useMemo(
    () => new Set([...calendarDateStatuses].filter(([, status]) => status === 'ready').map(([date]) => date)),
    [calendarDateStatuses],
  );
  const applicationJobs = getApplicationJobs(calendar, applications);

  useEffect(() => {
    setBulkSelectedDates((previous) => {
      const stillReady = new Set([...previous].filter((date) => readyDates.has(date)));
      return stillReady.size === previous.size ? previous : stillReady;
    });
  }, [readyDates]);

  const selectCalendarDate = useCallback(
    (date: string) => {
      setSelectedDate(date);
      if (!calendar?.recommendations.some((recommendation) => recommendation.travelDate === date)) return;
      recommendationScrollSequence.current += 1;
      setRecommendationScrollRequest({ date, sequence: recommendationScrollSequence.current });
    },
    [calendar?.recommendations],
  );
  const selectRecommendationDate = useCallback((date: string) => {
    setSelectedDate(date);
    setRecommendationScrollRequest(undefined);
  }, []);
  const toggleBulkSelectedDate = useCallback(
    (date: string) => {
      if (!readyDates.has(date)) return;
      setBulkSelectedDates((previous) => {
        const next = new Set(previous);
        if (next.has(date)) next.delete(date);
        else next.add(date);
        return next;
      });
    },
    [readyDates],
  );

  function reviewBulkSelection() {
    if (!calendar) return;
    const selectedApprovals = calendar.surchargeQuotes
      .filter((quote) => bulkSelectedDates.has(quote.travelDate) && readyDates.has(quote.travelDate))
      .map((quote) => ({
        offerId: calendar.offerId,
        roomTypeId: calendar.selectedRoomTypeId,
        checkInMonth: calendar.selectedCheckInMonth,
        generatedAt: calendar.generatedAt,
        quote,
      }));
    if (selectedApprovals.length) setBulkApprovals(selectedApprovals);
  }

  return (
    <Stack spacing={3}>
      <PageSubheader title="Heatmap surcharge suggestions">
        <Button
          variant="text"
          startIcon={<Refresh />}
          disabled={offers.isFetching || calendarQuery.isFetching}
          onClick={() => {
            void offers.refetch();
            void queryClient.invalidateQueries({ queryKey: heatmapSurchargeQueryKeys.calendars() });
            if (offerId)
              void queryClient.invalidateQueries({ queryKey: ['lere', 'heatmap-surcharge', 'pdf', offerId] });
          }}
        >
          Refresh
        </Button>
      </PageSubheader>
      <Typography color="text.secondary">
        Review the latest saved forecast for an offer, room type and check-in month.
      </Typography>
      {applicationJobs.map((application) => (
        <HeatmapApplicationStatus key={application.jobId} {...application} onStatusChange={updateApplicationStatus} />
      ))}
      {offers.isError && <Alert severity="error">Offers could not be loaded. {offers.error.message}</Alert>}
      {!offers.isPending && !offers.isError && !offers.data?.offers.length && (
        <Alert severity="info">No completed heatmaps are available yet.</Alert>
      )}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1.2fr 1fr 0.8fr' }} gap={2}>
        <Autocomplete
          options={offers.data?.offers ?? []}
          value={selectedOffer}
          loading={offers.isFetching}
          isOptionEqualToValue={(option, value) => option.offerId === value.offerId}
          getOptionLabel={(offer) => `${offer.offerId} — ${offer.vendorName || offer.offerName || 'Offer'}`}
          onChange={(_, offer) => selectFilters({ offerId: offer?.offerId })}
          renderInput={(inputParams) => (
            <TextField {...inputParams} label="Offer ID" placeholder="Search offer ID or hotel" />
          )}
        />
        <TextField
          select
          label="Room type"
          value={calendar?.selectedRoomTypeId ?? ''}
          disabled={!calendar || calendarQuery.isFetching}
          onChange={(event) => selectFilters({ offerId, roomTypeId: event.target.value })}
        >
          {(calendar?.roomTypes ?? []).map((room) => (
            <MenuItem key={room.roomTypeId} value={room.roomTypeId}>
              {room.roomTypeName || room.roomTypeId}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Check-in month"
          value={calendar?.selectedCheckInMonth ?? ''}
          disabled={!calendar || calendarQuery.isFetching}
          onChange={(event) =>
            selectFilters({ offerId, roomTypeId: calendar.selectedRoomTypeId, checkInMonth: event.target.value })
          }
        >
          {(calendar?.checkInMonths ?? []).map((month) => (
            <MenuItem key={month} value={month}>
              {formatMonth(month)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {!offerId && (
        <Alert severity="info">Select an offer to view its room types, recommendations and forecast calendar.</Alert>
      )}
      {offerId && calendarQuery.isPending && (
        <Stack spacing={2} aria-label="Loading heatmap calendar">
          <Skeleton height={50} />
          <Skeleton variant="rounded" height={400} />
        </Stack>
      )}
      {offerId && calendarQuery.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => selectFilters({ offerId })}>Reset room and month</Button>}
        >
          The calendar could not be loaded. {calendarQuery.error.message}
        </Alert>
      )}
      {calendar && (
        <>
          <Box>
            <Typography variant="h6">{calendar.vendorName || calendar.offerName || calendar.offerId}</Typography>
            <Typography variant="body2" color="text.secondary">
              {calendar.offerName}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1}>
              <Typography variant="caption">
                Forecast generated: {dayjs(calendar.generatedAt).format('D MMM YYYY, HH:mm')}
              </Typography>
              <Typography variant="caption">
                Pricing updated:{' '}
                {calendar.pricingContext.preparedAt
                  ? dayjs(calendar.pricingContext.preparedAt).format('D MMM YYYY, HH:mm')
                  : 'Unavailable'}
              </Typography>
            </Stack>
          </Box>
          {calendar.pricingContext.status !== 'ready' && (
            <Alert severity="warning">{pricingMessages[calendar.pricingContext.status]}</Alert>
          )}
          {calendarQuery.isFetching && <LinearProgress aria-label="Refreshing heatmap" />}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.35fr) minmax(360px, 1fr)' }}
            gap={3}
            alignItems="start"
          >
            <HeatmapRecommendations
              calendar={calendar}
              selectedDate={activeDate}
              onSelectDate={selectRecommendationDate}
              scrollRequest={recommendationScrollRequest}
              selectableDates={readyDates}
              selectedDates={bulkSelectedDates}
              onToggleSelectedDate={toggleBulkSelectedDate}
              onSelectAll={() => setBulkSelectedDates(new Set(readyDates))}
              onClearSelection={() => setBulkSelectedDates(new Set())}
              onReviewSelected={reviewBulkSelection}
              renderAction={(quote) => {
                const reviewedApproval: HeatmapSurchargeApproval = {
                  offerId: calendar.offerId,
                  roomTypeId: calendar.selectedRoomTypeId,
                  checkInMonth: calendar.selectedCheckInMonth,
                  generatedAt: calendar.generatedAt,
                  quote,
                };
                const submitted =
                  applications[getApprovalKey(calendar.offerId, calendar.selectedRoomTypeId, quote.travelDate)];
                if (quote.approval) {
                  const recordedStatus = quote.approval.status;
                  return (
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        {recordedStatus === 'applied'
                          ? 'Applied'
                          : recordedStatus === 'needs_review'
                            ? 'Review previous outcome'
                            : 'Submitted'}
                      </Typography>
                      {recordedStatus === 'needs_review' && (
                        <Typography variant="caption">
                          Check the Hotels Change Log before any further action.
                        </Typography>
                      )}
                    </Stack>
                  );
                }
                if (isApprovalBlocked(submitted, reviewedApproval))
                  return <Typography variant="body2">Submitted — see job status above</Typography>;
                return (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={
                      calendarDateStatuses.get(quote.travelDate) !== 'ready' ||
                      calendarQuery.isFetching ||
                      calendarQuery.isError
                    }
                    onClick={() => {
                      setSelectedDate(quote.travelDate);
                      setApproval(reviewedApproval);
                    }}
                  >
                    Review
                  </Button>
                );
              }}
            />
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 2 }, alignSelf: 'start' }}>
              <HeatmapCalendar
                month={calendar.selectedCheckInMonth}
                roomTypeName={selectedRoom?.roomTypeName || calendar.selectedRoomTypeId}
                roomNights={calendar.roomNights}
                dateStatuses={calendarDateStatuses}
                bulkSelectedDates={bulkSelectedDates}
                selectedDate={activeDate}
                onSelectDate={selectCalendarDate}
                onToggleBulkSelectedDate={toggleBulkSelectedDate}
              >
                <HeatmapPdfLink offerId={calendar.offerId} generatedAt={calendar.generatedAt} />
              </HeatmapCalendar>
            </Box>
          </Box>
          {approval && (
            <HeatmapApprovalDialog
              approval={approval}
              roomTypeName={
                calendar.roomTypes.find((room) => room.roomTypeId === approval.roomTypeId)?.roomTypeName ||
                approval.roomTypeId
              }
              onClose={() => setApproval(undefined)}
              onQueued={(jobId) => {
                const key = getApprovalKey(approval.offerId, approval.roomTypeId, approval.quote.travelDate);
                const roomName =
                  calendar.roomTypes.find((room) => room.roomTypeId === approval.roomTypeId)?.roomTypeName ||
                  approval.roomTypeId;
                setApplications((previous) => ({
                  ...previous,
                  [key]: {
                    jobId,
                    targetInternalSurcharge: approval.quote.targetInternalSurcharge,
                    status: 'pending',
                    label: `${roomName} · ${dayjs(approval.quote.travelDate).format('D MMM YYYY')} · ${
                      approval.offerId
                    }`,
                  },
                }));
                setBulkSelectedDates((previous) => {
                  const next = new Set(previous);
                  next.delete(approval.quote.travelDate);
                  return next;
                });
                setApproval(undefined);
              }}
            />
          )}
          {bulkApprovals && (
            <HeatmapBulkApprovalDialog
              approvals={bulkApprovals}
              roomTypeName={selectedRoom?.roomTypeName || calendar.selectedRoomTypeId}
              onClose={() => setBulkApprovals(undefined)}
              onSubmitted={(response) => {
                const submittedResults = response.results.filter(
                  (result): result is typeof result & { jobId: string } =>
                    result.status === 'submitted' && result.jobId !== null,
                );
                setApplications((previous) => {
                  const next = { ...previous };
                  for (const result of submittedResults) {
                    const submittedApproval = bulkApprovals.find(
                      (candidate) => candidate.quote.travelDate === result.travelDate,
                    );
                    if (!submittedApproval) continue;
                    const key = getApprovalKey(
                      submittedApproval.offerId,
                      submittedApproval.roomTypeId,
                      result.travelDate,
                    );
                    next[key] = {
                      jobId: result.jobId,
                      targetInternalSurcharge: submittedApproval.quote.targetInternalSurcharge,
                      status: 'pending',
                      label: `${selectedRoom?.roomTypeName || calendar.selectedRoomTypeId} · ${dayjs(
                        result.travelDate,
                      ).format('D MMM YYYY')} · ${calendar.offerId}`,
                    };
                  }
                  return next;
                });
                setBulkSelectedDates((previous) => {
                  const next = new Set(previous);
                  response.results
                    .filter((result) => result.status !== 'failed')
                    .forEach((result) => next.delete(result.travelDate));
                  return next;
                });
              }}
            />
          )}
        </>
      )}
    </Stack>
  );
}
